import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import ConnectionModalComponent, {PHASES} from '../components/connection-modal/connection-modal.jsx';
import VM from 'openblock-vm';
import analytics from '../lib/analytics';
import {connect} from 'react-redux';
import {closeConnectionModal} from '../reducers/modals';
import {
    setConnectionModalPeripheralName,
    setListAll,
    setUsbChecking,
    setUsbStatus,
    clearUsbStatus
} from '../reducers/connection-modal';

class ConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleScanning',
            'handleCancel',
            'handleConnected',
            'handleConnecting',
            'handleDisconnect',
            'handleError',
            'handleHelp',
            'handleRequestUsbPort'
        ]);
        this.state = {
            device: this.props.deviceData.find(device => device.deviceId === props.deviceId),
            phase: props.vm.getPeripheralIsConnected(props.deviceId) ?
                PHASES.connected : PHASES.scanning,
            peripheralName: null,
            errorMessage: null
        };
    }
    componentDidMount () {
        this.props.vm.on('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.on('PERIPHERAL_REQUEST_ERROR', this.handleError);
        this.checkUsbPorts();
    }
    /**
     * Verifica portas USB (Web Serial) ao abrir o modal, para exibir se a placa já está acessível.
     */
    checkUsbPorts () {
        const device = this.state.device;
        if (!device || !device.serialportRequired) return;
        this.props.dispatch(setUsbChecking());
        if (typeof navigator !== 'undefined' && navigator.serial && typeof navigator.serial.getPorts === 'function') {
            navigator.serial.getPorts()
                .then(ports => {
                    this.props.dispatch(setUsbStatus(ports.length));
                })
                .catch(err => {
                    this.props.dispatch(setUsbStatus(0, err && err.message ? err.message : 'Erro ao verificar portas'));
                });
        } else {
            this.props.dispatch(setUsbStatus(0, 'not_supported'));
        }
    }
    /**
     * Pede ao usuário que selecione uma porta USB (Web Serial). Assim a placa plugada passa a ser
     * "concedida" e getPorts() passará a listá-la nas próximas verificações.
     */
    handleRequestUsbPort () {
        const device = this.state.device;
        if (!device || !device.serialportRequired) return;
        if (typeof navigator === 'undefined' || !navigator.serial || typeof navigator.serial.requestPort !== 'function') {
            this.props.dispatch(setUsbStatus(0, 'not_supported'));
            return;
        }
        this.props.dispatch(setUsbChecking());
        navigator.serial.requestPort()
            .then(port => port.close())
            .then(() => navigator.serial.getPorts())
            .then(ports => {
                this.props.dispatch(setUsbStatus(ports.length));
            })
            .catch(err => {
                const msg = err && err.message ? err.message : String(err);
                this.props.dispatch(setUsbStatus(0, msg));
            });
    }
    componentWillUnmount () {
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.removeListener('PERIPHERAL_REQUEST_ERROR', this.handleError);
    }
    handleScanning () {
        this.setState({
            phase: PHASES.scanning
        });
    }
    handleConnecting (peripheralId, peripheralName) {
        if (this.props.isRealtimeMode) {
            this.props.vm.connectPeripheral(this.props.deviceId, peripheralId);
        } else {
            this.props.vm.connectPeripheral(this.props.deviceId, peripheralId, parseInt(this.props.baudrate, 10));
        }
        this.setState({
            phase: PHASES.connecting,
            peripheralName: peripheralName
        });
        analytics.event({
            category: 'devices',
            action: 'connecting',
            label: this.props.deviceId
        });
    }
    handleDisconnect () {
        try {
            this.props.vm.disconnectPeripheral(this.props.deviceId);
        } finally {
            this.props.onCancel();
        }
    }
    handleCancel () {
        try {
            // If we're not connected to a peripheral, close the websocket so we stop scanning.
            if (!this.props.vm.getPeripheralIsConnected(this.props.deviceId)) {
                this.props.vm.disconnectPeripheral(this.props.deviceId);
            }
        } finally {
            // Close the modal.
            this.props.onCancel();
        }
    }
    handleError (err) {
        // Assume errors that come in during scanning phase are the result of not
        // having scratch-link installed.
        if (this.state.phase === PHASES.scanning || this.state.phase === PHASES.unavailable) {
            this.setState({
                phase: PHASES.unavailable
            });
        } else {
            this.setState({
                phase: PHASES.error,
                errorMessage: err.message
            });
            analytics.event({
                category: 'devices',
                action: 'connecting error',
                label: this.props.deviceId
            });
        }
    }
    handleConnected () {
        this.setState({
            phase: PHASES.connected
        });
        analytics.event({
            category: 'devices',
            action: 'connected',
            label: this.props.deviceId
        });
        this.props.onConnected(this.state.peripheralName);
    }
    handleHelp () {
        window.open(this.state.device.helpLink, '_blank');
        analytics.event({
            category: 'devices',
            action: 'device help',
            label: this.props.deviceId
        });
    }
    render () {
        return (
            <ConnectionModalComponent
                connectingMessage={this.state.device && this.state.device.connectingMessage}
                connectionIconURL={this.state.device && this.state.device.connectionIconURL}
                connectionSmallIconURL={this.state.device && this.state.device.connectionSmallIconURL}
                errorMessage={this.state.errorMessage}
                isSerialport={this.state.device && this.state.device.serialportRequired}
                isListAll={this.props.isListAll}
                usbPortCount={this.props.usbPortCount}
                usbError={this.props.usbError}
                usbChecking={this.props.usbChecking}
                onRequestUsbPort={this.handleRequestUsbPort}
                connectionTipIconURL={this.state.device && this.state.device.connectionTipIconURL}
                deviceId={this.props.deviceId}
                name={this.state.device && this.state.device.name}
                phase={this.state.phase}
                title={this.props.deviceId}
                useAutoScan={this.state.device && this.state.device.useAutoScan}
                vm={this.props.vm}
                onCancel={this.handleCancel}
                onConnected={this.handleConnected}
                onConnecting={this.handleConnecting}
                onClickListAll={this.props.onClickListAll}
                onDisconnect={this.handleDisconnect}
                onHelp={this.handleHelp}
                onScanning={this.handleScanning}
            />
        );
    }
}

ConnectionModal.propTypes = {
    baudrate: PropTypes.string.isRequired,
    deviceId: PropTypes.string.isRequired,
    deviceData: PropTypes.instanceOf(Array).isRequired,
    dispatch: PropTypes.func.isRequired,
    isRealtimeMode: PropTypes.bool,
    isListAll: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    usbPortCount: PropTypes.number,
    usbError: PropTypes.string,
    usbChecking: PropTypes.bool,
    onConnected: PropTypes.func.isRequired,
    onClickListAll: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    baudrate: state.scratchGui.hardwareConsole.baudrate,
    deviceData: state.scratchGui.deviceData.deviceData,
    deviceId: state.scratchGui.device.deviceId,
    isRealtimeMode: state.scratchGui.programMode.isRealtimeMode,
    isListAll: state.scratchGui.connectionModal.isListAll,
    usbPortCount: state.scratchGui.connectionModal.usbPortCount,
    usbError: state.scratchGui.connectionModal.usbError,
    usbChecking: state.scratchGui.connectionModal.usbChecking
});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onCancel: () => {
        dispatch(clearUsbStatus());
        dispatch(closeConnectionModal());
    },
    onConnected: peripheralName => {
        dispatch(setConnectionModalPeripheralName(peripheralName));
    },
    onClickListAll: state => {
        dispatch(setListAll(state));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ConnectionModal);
